/**
 * Shared component style tokens (ADR-0010 S15) — the runtime mirror of the
 * backend `hub.apps.page_style`.
 *
 * Two layers:
 *  - **Universal** tokens (every component): box model + text — applied by the
 *    renderer as a wrapper class string (`styleToClasses`).
 *  - **Component-specific** tokens (per node type): e.g. button size/width,
 *    image fit/aspect, columns gap/align — applied by the component's OWN
 *    render case via `componentStyleClasses` (they replace baked-in defaults,
 *    so they live on the element, not the wrapper).
 *
 * This file is the SINGLE frontend source: each token carries its option list,
 * human label and the literal Tailwind class it maps to — the inspector's
 * `StylePanel` renders the controls from it AND the renderer maps tokens →
 * classes from it. Class strings are LITERALS so Tailwind's JIT scanner emits
 * them (`mt-${n}` would be purged). The token *vocabulary* (field + option
 * values) is mirrored in `backend/src/hub/apps/page_style.py`, which is the
 * save-time validation boundary — add a token in BOTH.
 */

export type StyleProps = Record<string, unknown>;

/** One selectable value of a style control + the class it maps to. `sw` is an
 *  optional swatch-preview class (for colour pickers, where the applied class
 *  is `text-*`/`bg-*` but the dot we draw is always a `bg-*`). */
export interface StyleOption {
  value: string;
  label: string;
  cls: string;
  sw?: string;
}

/** A single style control. `field` is the token key; the FIRST option is the
 *  implicit default (selecting it clears the key → the node round-trips
 *  un-styled for that token). */
export interface StyleControl {
  field: string;
  label: string;
  kind: "icons" | "segmented" | "swatch";
  options: StyleOption[];
}

/** A boolean (presence-flag) style token. */
export interface StyleBool {
  field: string;
  label: string;
  cls: string;
}

/** Per-direction spacing (padding / margin) — Elementor-style. Unlike the
 *  enum tokens, these hold NUMERIC per-side values + a unit and are applied as
 *  INLINE style (arbitrary values can't be Tailwind classes). Safe: numbers +
 *  a closed unit set only, never a free string. */
export interface DimValue {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  unit?: string;
}

export const DIM_UNITS = ["px", "%", "em", "rem"] as const;
export const DIM_SIDES = ["top", "right", "bottom", "left"] as const;

/** The dimension tokens + the CSS property each maps to. */
export const DIMENSIONS: { field: string; label: string; css: "padding" | "margin" }[] = [
  { field: "padding", label: "Padding", css: "padding" },
  { field: "margin", label: "Margin", css: "margin" },
];

/** Universal controls, grouped into inspector sections. */
export const UNIVERSAL_GROUPS: { title: string; controls: StyleControl[] }[] = [
  {
    title: "Text",
    controls: [
      {
        field: "align",
        label: "Align",
        kind: "icons",
        options: [
          { value: "left", label: "Left", cls: "" },
          { value: "center", label: "Center", cls: "text-center" },
          { value: "right", label: "Right", cls: "text-right" },
        ],
      },
      {
        field: "text_size",
        label: "Text size",
        kind: "segmented",
        options: [
          { value: "base", label: "M", cls: "" },
          { value: "xs", label: "XS", cls: "text-xs" },
          { value: "sm", label: "S", cls: "text-sm" },
          { value: "lg", label: "L", cls: "text-lg" },
          { value: "xl", label: "XL", cls: "text-xl" },
          { value: "2xl", label: "2XL", cls: "text-2xl" },
        ],
      },
      {
        field: "font_weight",
        label: "Weight",
        kind: "segmented",
        options: [
          { value: "normal", label: "Reg", cls: "" },
          { value: "medium", label: "Med", cls: "font-medium" },
          { value: "semibold", label: "Semi", cls: "font-semibold" },
          { value: "bold", label: "Bold", cls: "font-bold" },
        ],
      },
      {
        field: "text_color",
        label: "Color",
        kind: "swatch",
        options: [
          { value: "default", label: "Default", cls: "", sw: "bg-foreground" },
          { value: "muted", label: "Muted", cls: "text-muted-foreground", sw: "bg-muted-foreground" },
          { value: "accent", label: "Accent", cls: "text-accent", sw: "bg-accent" },
          { value: "danger", label: "Danger", cls: "text-danger", sw: "bg-danger" },
          { value: "success", label: "Success", cls: "text-success", sw: "bg-success" },
          { value: "warning", label: "Warning", cls: "text-warning", sw: "bg-warning" },
        ],
      },
    ],
  },
  {
    title: "Box",
    controls: [
      {
        field: "bg",
        label: "Background",
        kind: "swatch",
        options: [
          { value: "none", label: "None", cls: "", sw: "bg-transparent border border-border" },
          { value: "surface", label: "Surface", cls: "bg-surface", sw: "bg-surface" },
          { value: "elevated", label: "Elevated", cls: "bg-elevated", sw: "bg-elevated" },
          { value: "muted", label: "Muted", cls: "bg-muted/40", sw: "bg-muted" },
          { value: "accent", label: "Accent", cls: "bg-accent/10", sw: "bg-accent" },
        ],
      },
      {
        field: "radius",
        label: "Radius",
        kind: "segmented",
        options: [
          { value: "none", label: "0", cls: "" },
          { value: "sm", label: "S", cls: "rounded-sm" },
          { value: "md", label: "M", cls: "rounded-md" },
          { value: "lg", label: "L", cls: "rounded-lg" },
          { value: "full", label: "Full", cls: "rounded-full" },
        ],
      },
    ],
  },
];

/** Universal boolean flags. `hidden` has no class (the renderer handles it). */
export const UNIVERSAL_BOOLS: StyleBool[] = [
  { field: "border", label: "Border", cls: "border border-border" },
  { field: "hidden", label: "Hidden", cls: "" },
];

/** Per-breakpoint visibility (S15b). Static Tailwind media-query classes (the
 *  literals must appear here so the JIT generates them): hide a node only on a
 *  device size at RUNTIME. mobile < 640 (`max-sm`), tablet 640–1023
 *  (`sm:max-lg`), desktop ≥ 1024 (`lg`). In the builder (which previews via a
 *  width wrapper, not the real viewport) they don't apply — everything stays
 *  visible/selectable; the effect shows in the live portal. */
export const RESPONSIVE_BOOLS: StyleBool[] = [
  { field: "hide_mobile", label: "Hide on mobile", cls: "max-sm:hidden" },
  { field: "hide_tablet", label: "Hide on tablet", cls: "sm:max-lg:hidden" },
  { field: "hide_desktop", label: "Hide on desktop", cls: "lg:hidden" },
];

/** Component-specific controls, keyed by node type. Their classes replace the
 *  component's baked-in defaults, so the FIRST option of each restores the
 *  original look. */
export const COMPONENT_GROUPS: Record<string, StyleControl[]> = {
  button: [
    {
      field: "size",
      label: "Size",
      kind: "segmented",
      options: [
        { value: "md", label: "M", cls: "px-3 py-1.5 text-sm" },
        { value: "sm", label: "S", cls: "px-2 py-1 text-xs" },
        { value: "lg", label: "L", cls: "px-5 py-2.5 text-base" },
      ],
    },
    {
      field: "width",
      label: "Width",
      kind: "segmented",
      options: [
        { value: "auto", label: "Auto", cls: "w-fit" },
        { value: "full", label: "Full", cls: "w-full justify-center" },
      ],
    },
  ],
  image: [
    {
      field: "fit",
      label: "Fit",
      kind: "segmented",
      options: [
        { value: "auto", label: "Auto", cls: "" },
        { value: "cover", label: "Cover", cls: "w-full object-cover" },
        { value: "contain", label: "Contain", cls: "w-full object-contain" },
      ],
    },
    {
      field: "aspect",
      label: "Aspect",
      kind: "segmented",
      options: [
        { value: "auto", label: "Auto", cls: "" },
        { value: "square", label: "1:1", cls: "aspect-square w-full" },
        { value: "video", label: "16:9", cls: "aspect-video w-full" },
        { value: "wide", label: "21:9", cls: "aspect-[21/9] w-full" },
      ],
    },
  ],
  container: [
    {
      field: "gap",
      label: "Gap",
      kind: "segmented",
      options: [
        { value: "md", label: "M", cls: "gap-4" },
        { value: "none", label: "0", cls: "gap-0" },
        { value: "sm", label: "S", cls: "gap-2" },
        { value: "lg", label: "L", cls: "gap-6" },
      ],
    },
    {
      field: "align_items",
      label: "Align items",
      kind: "segmented",
      options: [
        { value: "stretch", label: "Stretch", cls: "" },
        { value: "start", label: "Start", cls: "items-start" },
        { value: "center", label: "Center", cls: "items-center" },
        { value: "end", label: "End", cls: "items-end" },
      ],
    },
    {
      field: "justify_content",
      label: "Justify",
      kind: "segmented",
      options: [
        { value: "start", label: "Start", cls: "" },
        { value: "center", label: "Center", cls: "justify-center" },
        { value: "end", label: "End", cls: "justify-end" },
        { value: "between", label: "Between", cls: "justify-between" },
        { value: "around", label: "Around", cls: "justify-around" },
      ],
    },
  ],
};

/** Component-specific boolean flags, keyed by node type. */
export const COMPONENT_BOOLS: Record<string, StyleBool[]> = {
  image: [{ field: "shadow", label: "Shadow", cls: "shadow-md" }],
};

// ---- mapping helpers ------------------------------------------------------

const ALL_UNIVERSAL = UNIVERSAL_GROUPS.flatMap((g) => g.controls);

/** True when a component has any styleable tokens beyond the universal set. */
export function hasComponentStyle(type: string): boolean {
  return (COMPONENT_GROUPS[type]?.length ?? 0) > 0 || (COMPONENT_BOOLS[type]?.length ?? 0) > 0;
}

/** Map the UNIVERSAL tokens of a style map to a space-joined wrapper class
 *  string. Default / unknown values contribute nothing, so `styleToClasses({})`
 *  is "". `hidden` is NOT emitted (the renderer omits the node instead). */
export function styleToClasses(style: StyleProps | undefined | null): string {
  if (!style) return "";
  const out: string[] = [];
  for (const ctrl of ALL_UNIVERSAL) {
    const val = String(style[ctrl.field] ?? ctrl.options[0].value);
    const opt = ctrl.options.find((o) => o.value === val);
    if (opt?.cls) out.push(opt.cls);
  }
  for (const b of [...UNIVERSAL_BOOLS, ...RESPONSIVE_BOOLS]) {
    if (b.cls && style[b.field] === true) out.push(b.cls);
  }
  return out.join(" ");
}

/** Map the COMPONENT-specific tokens of a node to a class string applied to
 *  the component's own element. Each control's first option is its default, so
 *  an absent token yields the component's baked-in look. */
export function componentStyleClasses(type: string, style: StyleProps | undefined | null): string {
  const s = style ?? {};
  const out: string[] = [];
  for (const ctrl of COMPONENT_GROUPS[type] ?? []) {
    const val = String(s[ctrl.field] ?? ctrl.options[0].value);
    const opt = ctrl.options.find((o) => o.value === val) ?? ctrl.options[0];
    if (opt.cls) out.push(opt.cls);
  }
  for (const b of COMPONENT_BOOLS[type] ?? []) {
    if (s[b.field] === true) out.push(b.cls);
  }
  return out.join(" ");
}

/** Build the INLINE style for the per-direction spacing tokens (padding /
 *  margin) — each present side becomes a `paddingTop: "8px"` etc. Numbers +
 *  unit only, so it's injection-safe. */
export function styleToInlineStyle(style: StyleProps | undefined | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!style) return out;
  for (const dim of DIMENSIONS) {
    const v = style[dim.field] as DimValue | undefined;
    if (!v || typeof v !== "object") continue;
    const unit = typeof v.unit === "string" && (DIM_UNITS as readonly string[]).includes(v.unit) ? v.unit : "px";
    for (const side of DIM_SIDES) {
      const n = v[side];
      if (typeof n === "number" && Number.isFinite(n)) {
        // paddingTop / marginRight / …
        const key = dim.css + side.charAt(0).toUpperCase() + side.slice(1);
        out[key] = `${n}${unit}`;
      }
    }
  }
  return out;
}

/** True when a node opts to be hidden (portal: not rendered). */
export function isHidden(style: StyleProps | undefined | null): boolean {
  return !!style && style.hidden === true;
}

export type Breakpoint = "mobile" | "tablet" | "desktop";

/** The responsive-visibility token that hides a node at a given breakpoint
 *  (S15b). Mirrors `RESPONSIVE_BOOLS` — the field the media-query class targets. */
const HIDE_FIELD_BY_BREAKPOINT: Record<Breakpoint, string> = {
  mobile: "hide_mobile",
  tablet: "hide_tablet",
  desktop: "hide_desktop",
};

/** True when the node is hidden at `bp` via a `hide_*` token. The builder uses
 *  this to GREY OUT (not remove) a node that the live portal would hide at the
 *  selected preview width — the viewport selector is a width preview, not a real
 *  media context, so the media-query class never fires there. */
export function isHiddenAtBreakpoint(
  style: StyleProps | undefined | null,
  bp: Breakpoint | undefined | null,
): boolean {
  if (!style || !bp) return false;
  return style[HIDE_FIELD_BY_BREAKPOINT[bp]] === true;
}

/** True when a style map carries any per-direction spacing value. */
export function hasInlineStyle(style: StyleProps | undefined | null): boolean {
  return Object.keys(styleToInlineStyle(style)).length > 0;
}

/** True when a style map carries any non-default UNIVERSAL token (drives
 *  whether the renderer wraps the node — component tokens live on the element,
 *  so they don't need a wrapper). */
export function hasStyle(style: StyleProps | undefined | null): boolean {
  return styleToClasses(style) !== "" || isHidden(style) || hasInlineStyle(style);
}
